import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { CSSProperties, ReactNode, Ref } from "react";
import { InlineCardFields } from "@libs/identity-ui";
import type { CardFieldsError, CardFieldsHandle } from "@libs/identity-ui";
import type { CardSummary, CardTransport } from "@libs/buyer-identity-client";
import type { CaptureAppearance } from "@libs/buyer-identity-client/capture-html";
import { chipChoosable } from "@libs/buy-on-behalf-client/cards";
import type { CardChoice, CardSlot } from "@libs/buy-on-behalf-client/cards";
import type { ResolvedAppearance } from "@libs/checkout-web/appearance";
import { CardBrandMark } from "@libs/checkout-web/card-brand";
import { SaveTick, SavedCardChip } from "@libs/checkout-web/card-chips";
import { Notice } from "@libs/checkout-web/layout";
import { CONTROL_HEIGHT, Field, input as inputStyle, shimmer, text } from "@libs/checkout-web/ui";

/**
 * The card, for a vendor checkout, laid out as the engine's card island
 * draws it (apps/ticketing-checkout/src/island/card-island.tsx): the card
 * number with the saved Lissen card's chip (or the Save with Lissen tick)
 * inside it, and the expiry and code side by side under it. Only the
 * provider differs: VGS's secure fields instead of Stripe's.
 *
 * - The chip ticked, the saved card pays: its number and expiry read back
 *   in their own fields, and its code is either still held (fresh enough
 *   for the browser's run) or asked for again in the code's own field —
 *   VGS's secure field for that one card.
 * - The chip unticked (or no saved card), VGS's new-card fields take the
 *   same three places; a card entered here is saved with Lissen as it is
 *   tokenised, so the tick is shown ticked and fixed. Pressing the saved
 *   card's number or expiry chooses a new card, as typing one does in the
 *   engine's.
 * - A saved card of a brand the vendor does not take (`slot.refusal`) is
 *   never chosen by the chip, and the slot says why under the fields: in
 *   the danger notice when it is the card chosen (a new card captured of
 *   that brand), as a hint beside a new card otherwise.
 *
 * The new card's secure fields are always there, loaded, as the app's are
 * (apps/mobile/components/Checkout/vendor/VendorCardSlot.tsx): under the
 * saved card they are unseen, and its number and expiry let a click
 * through to them, so one click puts the caret in the field it lands on,
 * and that focus is choosing the new card. Under the read-back they are
 * not the checkout's handle and report nothing: nothing would be captured.
 * Until they have loaded (or when they could not), the read-back takes
 * the click and chooses the new card itself.
 *
 * Card numbers and codes live only in VGS's frames: this component holds
 * nothing but the handle that asks them to tokenise. What the secure fields
 * could not do is said under all the card's fields, full width, in the
 * checkout's notice, with the way to load them again; never in a field's
 * own box.
 */
export function VendorCardSlot({
  slot,
  transport,
  appearance,
  disabled,
  fields,
  onChoose,
  onValidity,
}: {
  slot: CardSlot;
  transport: CardTransport;
  appearance: ResolvedAppearance;
  disabled: boolean;
  fields: Ref<CardFieldsHandle>;
  onChoose: (choice: CardChoice) => void;
  onValidity: (valid: { key: string; valid: boolean }) => void;
}) {
  // The one chip: the card that pays, or the saved one to go back to.
  const chipCard = slot.chip;
  // Another chip is another width to keep free: measured afresh.
  return (
    <CardLayers
      key={chipCard ? "chip" : "tick"}
      slot={slot}
      transport={transport}
      appearance={appearance}
      disabled={disabled}
      fields={fields}
      onChoose={onChoose}
      onValidity={onValidity}
      chipCard={chipCard}
    />
  );
}

/** Before the chip has been measured (and where nothing is laid out): the
 *  chip's and the tick's widths in the checkout's own type. */
const CHIP_RESERVE = 132;
const TICK_RESERVE = 124;
/** Between the typed number's end and the chip. */
const CHIP_GAP = 8;
/** What the card number keeps however narrow the checkout: its padding,
 *  the brand icon and about nine digits. The chip gives way first. */
const NUMBER_MIN_PX = 132;

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * The new card's fields, the saved card's read-back over them, and the chip
 * (or tick) over both, inside the number field's trailing end, as the
 * engine draws its chip inside Stripe's: the secure field keeps that room
 * free (`numberTrailingPadding`), so a typed number scrolls within what is
 * left and never runs under the chip. The room is measured once, as the
 * fields mount and before VGS's page has loaded: a later change would
 * reload the secure page and lose what was typed. However narrow the
 * checkout, the number keeps NUMBER_MIN_PX; the chip is clipped before the
 * number is.
 */
function CardLayers({
  slot,
  transport,
  appearance,
  disabled,
  fields,
  onChoose,
  onValidity,
  chipCard,
}: Parameters<typeof VendorCardSlot>[0] & { chipCard: CardSummary | null }) {
  const saved = slot.selected;
  const chipBox = useRef<HTMLDivElement>(null);
  const [reserve, setReserve] = useState(
    (chipCard ? CHIP_RESERVE : TICK_RESERVE) + CHIP_GAP,
  );
  useIsomorphicLayoutEffect(() => {
    const width = chipBox.current?.getBoundingClientRect().width ?? 0;
    if (width > 0) setReserve(Math.ceil(width) + CHIP_GAP);
  }, []);
  // Stable per card: the secure fields re-announce their validity whenever
  // this callback changes, so a fresh one each render would never settle.
  const fieldsKey = slot.fieldsKey;
  const report = useCallback(
    (valid: boolean) => onValidity({ key: fieldsKey, valid }),
    [onValidity, fieldsKey],
  );
  const attachNew = useCallback(
    (handle: CardFieldsHandle | null) => {
      if (!saved) assignRef(fields, handle);
    },
    [saved, fields],
  );
  const chooseNew = useCallback(
    ({ focused }: { focused: boolean }) => {
      if (focused && saved) onChoose({ cardId: saved.id, newCard: true });
    },
    [saved, onChoose],
  );
  const [newReady, setNewReady] = useState(false);
  const [newError, setNewError] = useState<CardFieldsError | null>(null);
  const [codeError, setCodeError] = useState<CardFieldsError | null>(null);
  // The saved card's code field, once asked for, stays for that card.
  const [codeFor, setCodeFor] = useState<string | null>(null);
  if (saved && slot.fields === "cvc" && codeFor !== saved.id)
    setCodeFor(saved.id);
  const asksCode = !!saved && slot.fields === "cvc";
  // The saved card's code until it is typed: light orange, shimmering, so
  // the buyer sees it is the one field left to fill.
  const [codeReady, setCodeReady] = useState(false);
  const [codeGiven, setCodeGiven] = useState(false);
  const reportCode = useCallback(
    (valid: boolean) => {
      setCodeGiven(valid);
      report(valid);
    },
    [report],
  );
  const codeWanted = asksCode && codeReady && !codeGiven && !disabled;
  // Until then a tap on it does nothing: it must not look ready to take one.
  const codeLoading = asksCode && !codeReady && !codeError;
  const code = chipCard && codeFor === chipCard.id ? chipCard : null;
  const shown = saved ? chipCard : null;
  // A click reaches the new card's fields only once they are there, and
  // never while the checkout is busy.
  const passThrough = newReady && !newError && !disabled;
  const fieldsError = saved ? (asksCode ? codeError : null) : newError;

  const chip = chipCard ? (
    <SavedCardChip
      card={{
        id: chipCard.id,
        brand: chipCard.brand.toLowerCase(),
        last4: chipCard.last4,
        exp_month: chipCard.expiryMonth,
        exp_year: chipCard.expiryYear,
      }}
      selected={saved !== null}
      // A card the vendor does not take is not chosen: only given up.
      disabled={disabled || !chipChoosable(slot)}
      onToggle={() =>
        onChoose(
          saved
            ? { cardId: saved.id, newCard: true }
            : { cardId: chipCard.id, newCard: false },
        )
      }
    />
  ) : (
    <SaveTick checked disabled onChange={() => undefined} />
  );
  const expiry = chipCard
    ? `${String(chipCard.expiryMonth).padStart(2, "0")} / ${String(chipCard.expiryYear % 100).padStart(2, "0")}`
    : "";
  const useAnother = () => {
    if (!disabled && saved) onChoose({ cardId: saved.id, newCard: true });
  };
  const readBackCell = (children: ReactNode, label: string) => (
    <div
      style={{
        ...frame,
        pointerEvents: shown && !passThrough ? "auto" : "none",
      }}
      role="button"
      tabIndex={disabled || !shown ? -1 : 0}
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={useAnother}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          useAnother();
        }
      }}
    >
      {children}
    </div>
  );

  return (
    <Field label={null}>
      <div style={{ position: "relative" }}>
        <div style={saved ? under : undefined}>
          <InlineCardFields
            key="new"
            ref={attachNew}
            transport={transport}
            appearance={{
              ...captureLook(appearance, "split"),
              numberTrailingPadding: reserve,
            }}
            onValidityChange={saved ? undefined : report}
            onFieldState={chooseNew}
            onReadyChange={setNewReady}
            onError={setNewError}
          />
        </div>
        {chipCard ? (
          <div
            aria-hidden={shown ? undefined : true}
            data-lc-card-saved={shown ? "" : undefined}
            style={{ ...over, ...(shown ? null : gone) }}
          >
            {readBackCell(
              <>
                <CardBrandMark
                  brand={chipCard.brand.toLowerCase()}
                  height={14}
                />
                <span style={{ ...readBack, paddingRight: reserve }}>
                  •••• {chipCard.last4}
                </span>
              </>,
              "Use another card",
            )}
            <div
              style={pair}
              role="group"
              aria-label="Expiry date and security code"
            >
              {readBackCell(
                <span style={readBack}>{expiry}</span>,
                `Expires ${expiry}. Use another card`,
              )}
              {code ? (
                <div
                  data-testid="card-security-code"
                  data-lc-code-wanted={codeWanted ? "" : undefined}
                  data-lc-shimmer={codeLoading ? "" : undefined}
                  style={
                    codeWanted
                      ? codeWantedLook
                      : codeLoading
                        ? { ...codeLook, ...shimmer() }
                        : codeLook
                  }
                >
                  <style>{CODE_WANTED_KEYFRAMES}</style>
                  <InlineCardFields
                    // The code is captured for this card and no other.
                    key={code.id}
                    ref={asksCode ? fields : undefined}
                    transport={transport}
                    cardId={code.id}
                    // Clear, so the box under it shows through.
                    appearance={{ ...captureLook(appearance, "row"), background: "transparent" }}
                    onValidityChange={asksCode ? reportCode : undefined}
                    onReadyChange={setCodeReady}
                    onError={setCodeError}
                  />
                </div>
              ) : (
                <div style={frame} aria-label="Security code saved">
                  <span style={readBack}>•••</span>
                </div>
              )}
            </div>
          </div>
        ) : null}
        {/* Inside the number field's right end, as in the engine's, over
            whichever card is shown: one chip, never mounted again. */}
        <div ref={chipBox} style={chipSlot}>
          {chip}
        </div>
      </div>
      {fieldsError ? (
        <Notice
          message={fieldsError.message}
          action={{ label: "Reload card fields", onClick: fieldsError.reload }}
        />
      ) : null}
      {/* Why the vendor will not take the saved card: in the way when it
          is the one chosen, a note beside a new card otherwise. */}
      {slot.refusal ? (
        saved ? (
          <Notice message={slot.refusal} />
        ) : (
          <p style={text.hint}>{slot.refusal}</p>
        )
      ) : null}
    </Field>
  );
}

/** Sets a ref of either kind (the checkout's handle). */
function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
}

const GAP = "calc(var(--lc-space) * 0.75)";

/** The new card's fields under the saved card's read-back: unseen, still
 *  taking the click that reaches them. */
const under: CSSProperties = { opacity: 0 };

/** The saved card's read-back, over the new card's fields. */
const over: CSSProperties = {
  display: "grid",
  gap: GAP,
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
};

/** The saved card's read-back once another card is chosen: kept, unseen,
 *  taking nothing. */
const gone: CSSProperties = { opacity: 0, pointerEvents: "none" };

const pair: CSSProperties = {
  display: "grid",
  gap: GAP,
  gridTemplateColumns: "1fr 1fr",
};

const frame: CSSProperties = {
  ...inputStyle,
  alignItems: "center",
  cursor: "default",
  display: "flex",
  fontSize: "var(--lc-input-font-size)",
  gap: "var(--lc-space)",
  minHeight: CONTROL_HEIGHT,
  minWidth: 0,
};

/** Under the saved card's clear code field: the input's own background. */
const codeLook: CSSProperties = {
  background: "var(--lc-input-background)",
  borderRadius: "var(--lc-input-radius)",
  minWidth: 0,
};

const CODE_WANTED_STRONG = "color-mix(in srgb, #ff9500 14%, var(--lc-input-background))";
const CODE_WANTED_FAINT = "color-mix(in srgb, #ff9500 4%, var(--lc-input-background))";

const CODE_WANTED_KEYFRAMES =
  `@keyframes lc-code-wanted{0%,100%{background-color:${CODE_WANTED_STRONG}}50%{background-color:${CODE_WANTED_FAINT}}}` +
  "@media (prefers-reduced-motion: reduce){[data-lc-code-wanted]{animation:none!important}}";

/** The code still to type: a light orange, pulsing (still, where motion is
 *  reduced). A pulse, not the loading shimmer's passing band, so it reads as
 *  asked rather than coming. */
const codeWantedLook: CSSProperties = {
  ...codeLook,
  animation: "lc-code-wanted 1.6s ease-in-out infinite",
  background: CODE_WANTED_STRONG,
};

const readBack: CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** Over the right end of the secure number field's first line. */
const chipSlot: CSSProperties = {
  alignItems: "center",
  display: "flex",
  height: CONTROL_HEIGHT,
  maxWidth: `calc(100% - ${NUMBER_MIN_PX}px - var(--lc-space) * 1.5)`,
  overflow: "hidden",
  position: "absolute",
  right: "calc(var(--lc-space) * 1.5)",
  top: 0,
};

/** The system face the checkout frame actually renders in; the secure
 *  fields cannot load the page's webfont either. */
const SYSTEM_FONT = "ui-sans-serif, system-ui, -apple-system, sans-serif";

/** The checkout's field tokens, for VGS's secure fields: a new card split
 *  into the engine's three fields, or a saved card's code as one. */
export function captureLook(
  appearance: ResolvedAppearance,
  layout: CaptureAppearance["layout"] = "split",
): Partial<CaptureAppearance> {
  const px = (value: string, fallback: number) => {
    const number = parseFloat(value);
    return Number.isFinite(number) && value.trim().endsWith("px")
      ? number
      : fallback;
  };
  return {
    layout,
    text: appearance.input.text,
    placeholder: appearance.input.placeholder,
    background: appearance.input.background,
    border: appearance.input.border,
    focusBorder: appearance.input.focusBorder,
    focusShadow: appearance.input.focusShadow,
    danger: appearance.colors.danger,
    radius: px(appearance.input.radius, 12),
    fontFamily: SYSTEM_FONT,
    fontSize: 16,
    height: CONTROL_HEIGHT,
    gap: px(appearance.spacing, 8) * 0.75,
  };
}
